import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Truck, MapPin } from "@phosphor-icons/react";

const DeliverySettings = () => {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/delivery/settings`);
      setS(res.data);
    } catch (e) {
      toast.error("Couldn't load delivery settings");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch = {}) => {
    setSaving(true);
    try {
      const merged = { ...s, ...patch };
      const res = await axios.put(`${API}/delivery/settings`, merged);
      setS(res.data);
      toast.success("Delivery settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !s) {
    return (
      <div className="text-sm text-muted-foreground py-6">Loading delivery settings…</div>
    );
  }

  const set = (k) => (v) => setS((prev) => ({ ...prev, [k]: v }));
  const num = (v) => (v === "" || v === null ? "" : Number(v));

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck size={22} weight="bold" /> First-Party Delivery
          </CardTitle>
          <CardDescription>
            Enable in-house delivery driven by your own drivers. Turning this
            on unlocks the Driver role, exposes delivery on the online
            ordering site, and shows the delivery-time slider on the POS.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <div className="font-medium">Enable First-Party Delivery</div>
              <div className="text-xs text-muted-foreground">Master switch for everything below.</div>
            </div>
            <Switch
              checked={!!s.first_party_delivery_enabled}
              onCheckedChange={(v) => save({ first_party_delivery_enabled: v })}
              data-testid="delivery-enabled-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Store address (geocoded server-side) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin size={22} weight="bold" /> Store Location
          </CardTitle>
          <CardDescription>
            Used as the origin for every distance & ETA calculation. Type your
            full street address and we&apos;ll look up the coordinates on save.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label>Address</Label>
            <div className="flex gap-2">
              <Input
                value={s.store_address || ""}
                onChange={(e) => set("store_address")(e.target.value)}
                placeholder="123 Main St, Springfield IL"
                data-testid="delivery-store-address"
              />
              <Button
                onClick={() => save({ store_address: s.store_address })}
                disabled={saving || !s.store_address}
                data-testid="delivery-geocode-btn"
              >
                Save & Geocode
              </Button>
            </div>
            {s.store_lat != null && s.store_lng != null && (
              <div className="text-xs text-muted-foreground" data-testid="delivery-geocoded-coords">
                Geocoded to {s.store_lat.toFixed(5)}, {s.store_lng.toFixed(5)}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Fees */}
      <Card>
        <CardHeader>
          <CardTitle>Delivery Fee</CardTitle>
          <CardDescription>Flat rate or percentage of subtotal. Applied on top of the order total.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label>Fee Type</Label>
            <Select value={s.delivery_fee_type || "flat"} onValueChange={set("delivery_fee_type")}>
              <SelectTrigger data-testid="delivery-fee-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="flat">Flat rate</SelectItem>
                <SelectItem value="percentage">Percentage</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Flat Rate ($)</Label>
            <Input
              type="number" step="0.01" min="0"
              value={s.delivery_fee_flat ?? ""}
              onChange={(e) => set("delivery_fee_flat")(num(e.target.value))}
              disabled={s.delivery_fee_type === "percentage"}
              data-testid="delivery-fee-flat"
            />
          </div>
          <div className="space-y-1">
            <Label>Percentage (%)</Label>
            <Input
              type="number" step="0.1" min="0" max="100"
              value={s.delivery_fee_percent ?? ""}
              onChange={(e) => set("delivery_fee_percent")(num(e.target.value))}
              disabled={s.delivery_fee_type !== "percentage"}
              data-testid="delivery-fee-percent"
            />
          </div>
        </CardContent>
      </Card>

      {/* Timing & Pay */}
      <Card>
        <CardHeader>
          <CardTitle>Timing & Driver Pay</CardTitle>
          <CardDescription>
            ETA = kitchen make time + travel time (at your avg speed) + on-the-fly cushion
            + per-active-delivery cushion. Driver mileage pay offsets their gas cost and shows on their earnings dashboard.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>Default Make Time (min)</Label>
            <Input
              type="number" min="0"
              value={s.delivery_make_time_min ?? ""}
              onChange={(e) => set("delivery_make_time_min")(num(e.target.value))}
              data-testid="delivery-make-time"
            />
          </div>
          <div className="space-y-1">
            <Label>Average Driving Speed (mph)</Label>
            <Input
              type="number" min="5" max="70"
              value={s.delivery_avg_speed_mph ?? ""}
              onChange={(e) => set("delivery_avg_speed_mph")(num(e.target.value))}
              data-testid="delivery-avg-speed"
            />
          </div>
          <div className="space-y-1">
            <Label>Add per Active Delivery (min)</Label>
            <Input
              type="number" min="0" max="30"
              value={s.delivery_busy_add_per_order_min ?? ""}
              onChange={(e) => set("delivery_busy_add_per_order_min")(num(e.target.value))}
              data-testid="delivery-busy-add"
            />
          </div>
          <div className="space-y-1">
            <Label>Driver Pay per Mile ($)</Label>
            <Input
              type="number" step="0.01" min="0"
              value={s.delivery_price_per_mile ?? ""}
              onChange={(e) => set("delivery_price_per_mile")(num(e.target.value))}
              placeholder="e.g. 0.65"
              data-testid="delivery-price-per-mile"
            />
          </div>
        </CardContent>
      </Card>

      {/* Distance & Order caps */}
      <Card>
        <CardHeader>
          <CardTitle>Range & Order Value</CardTitle>
          <CardDescription>Guardrails that reject bad orders before they reach the kitchen.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label>Max Delivery Distance (mi)</Label>
            <Input
              type="number" step="0.1" min="0"
              value={s.delivery_max_distance_mi ?? ""}
              onChange={(e) => set("delivery_max_distance_mi")(num(e.target.value))}
              data-testid="delivery-max-distance"
            />
          </div>
          <div className="space-y-1">
            <Label>Min Order Amount ($)</Label>
            <Input
              type="number" step="0.01" min="0"
              value={s.delivery_min_order_amount ?? ""}
              onChange={(e) => set("delivery_min_order_amount")(num(e.target.value))}
              data-testid="delivery-min-order"
            />
          </div>
          <div className="space-y-1">
            <Label>Max Order Amount ($, 0 = no cap)</Label>
            <Input
              type="number" step="0.01" min="0"
              value={s.delivery_max_order_amount ?? ""}
              onChange={(e) => set("delivery_max_order_amount")(num(e.target.value))}
              data-testid="delivery-max-order"
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => save()} disabled={saving} data-testid="delivery-save-btn">
          {saving ? "Saving…" : "Save All Changes"}
        </Button>
      </div>

      {/* Advanced — distance provider + auto-reassign */}
      <Card>
        <CardHeader>
          <CardTitle>Advanced</CardTitle>
          <CardDescription>
            Optional Google Maps upgrade for exact driving times, and automatic
            hand-off of open runs when a driver clocks out.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Distance & ETA Provider</Label>
            <Select
              value={s.delivery_distance_provider || "haversine"}
              onValueChange={set("delivery_distance_provider")}
            >
              <SelectTrigger data-testid="delivery-provider-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="haversine">Free — straight-line × road factor</SelectItem>
                <SelectItem value="google_maps">Google Maps (requires API key)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {s.delivery_distance_provider === "google_maps" && (
            <div className="space-y-2">
              <Label>Google Maps API Key</Label>
              <Input
                type="password"
                value={s.google_maps_api_key || ""}
                onChange={(e) => set("google_maps_api_key")(e.target.value)}
                placeholder="AIza…"
                data-testid="delivery-google-key"
              />
              <div className="text-xs text-muted-foreground">
                Enable both Distance Matrix API and Geocoding API in your
                Google Cloud project. Falls back to free provider on any error.
              </div>
            </div>
          )}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <div className="font-medium">Auto-reassign on driver clockout</div>
              <div className="text-xs text-muted-foreground">
                When a driver clocks out, hand their open runs to the nearest available driver.
              </div>
            </div>
            <Switch
              checked={s.delivery_auto_reassign_on_clockout !== false}
              onCheckedChange={(v) => save({ delivery_auto_reassign_on_clockout: v })}
              data-testid="delivery-auto-reassign-toggle"
            />
          </div>
        </CardContent>
      </Card>

      <DeliveryZones />
      <TestDeliveryCard />
    </div>
  );
};

export default DeliverySettings;

/**
 * DeliveryZones — nested card for managing circle-based neighborhood
 * overrides. Rendered inside DeliverySettings below the Advanced card.
 */
const DeliveryZones = () => {
  const [zones, setZones] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/delivery/zones`);
      setZones(res.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    try {
      if (editing.id) {
        await axios.put(`${API}/delivery/zones/${editing.id}`, editing);
      } else {
        await axios.post(`${API}/delivery/zones`, editing);
      }
      toast.success("Zone saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this zone?")) return;
    try {
      await axios.delete(`${API}/delivery/zones/${id}`);
      toast.success("Zone deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const blank = {
    name: "", address: "", radius_mi: 1.0,
    extra_fee: 0, extra_time_min: 0, min_order_override: null, priority: 100,
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Delivery Zones</CardTitle>
          <CardDescription>
            Circle-based overrides for a specific neighborhood. Lower priority wins on overlap.
          </CardDescription>
        </div>
        <Button size="sm" onClick={() => setEditing({ ...blank })} data-testid="add-zone-btn">
          + Add Zone
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {!loading && zones.length === 0 && (
          <div className="text-sm text-muted-foreground">No zones yet. Everyone pays the base fee.</div>
        )}
        {zones.map((z) => (
          <div key={z.id} className="rounded-lg border p-3 flex items-center gap-3" data-testid={`zone-${z.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium">{z.name}</div>
              <div className="text-xs text-muted-foreground">
                {z.radius_mi} mi radius · +${z.extra_fee?.toFixed(2)} · +{z.extra_time_min || 0} min · priority {z.priority}
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => setEditing(z)}>Edit</Button>
            <Button size="sm" variant="outline" className="text-destructive" onClick={() => remove(z.id)}>Delete</Button>
          </div>
        ))}
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="zone-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit Zone" : "New Zone"}</DialogTitle>
            <DialogDescription>
              Enter a center address (we&apos;ll geocode it) and a radius in miles.
              Any order inside the circle gets the extra fee and time added on top.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 py-2">
              <div className="space-y-1">
                <Label>Name</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="Downtown" data-testid="zone-name" />
              </div>
              <div className="space-y-1">
                <Label>Center Address</Label>
                <Input value={editing.address || ""} onChange={(e) => setEditing({ ...editing, address: e.target.value })}
                  placeholder="Main St & 5th, City" data-testid="zone-address" />
                {editing.center_lat && (
                  <div className="text-xs text-muted-foreground">
                    Current center: {editing.center_lat.toFixed(4)}, {editing.center_lng.toFixed(4)}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label>Radius (mi)</Label>
                  <Input type="number" step="0.1" min="0.1"
                    value={editing.radius_mi}
                    onChange={(e) => setEditing({ ...editing, radius_mi: Number(e.target.value) })}
                    data-testid="zone-radius" />
                </div>
                <div className="space-y-1">
                  <Label>Priority (lower wins)</Label>
                  <Input type="number" min="1" max="999"
                    value={editing.priority}
                    onChange={(e) => setEditing({ ...editing, priority: Number(e.target.value) })}
                    data-testid="zone-priority" />
                </div>
                <div className="space-y-1">
                  <Label>Extra Fee ($)</Label>
                  <Input type="number" step="0.01" min="0"
                    value={editing.extra_fee}
                    onChange={(e) => setEditing({ ...editing, extra_fee: Number(e.target.value) })}
                    data-testid="zone-fee" />
                </div>
                <div className="space-y-1">
                  <Label>Extra Time (min)</Label>
                  <Input type="number" min="0" max="60"
                    value={editing.extra_time_min}
                    onChange={(e) => setEditing({ ...editing, extra_time_min: Number(e.target.value) })}
                    data-testid="zone-time" />
                </div>
                <div className="space-y-1 col-span-2">
                  <Label>Min Order Override ($, blank = use global)</Label>
                  <Input type="number" step="0.01" min="0"
                    value={editing.min_order_override ?? ""}
                    onChange={(e) => setEditing({ ...editing, min_order_override: e.target.value === "" ? null : Number(e.target.value) })}
                    data-testid="zone-min-override" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} data-testid="zone-save-btn">Save Zone</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

/**
 * TestDeliveryCard — one-click "fake order at this address" so managers
 * can dry-run the full flow (quote → driver assignment → tracking page
 * → SMS on start) before flipping delivery on to real customers.
 *
 * Also owns the "Test Delivery Presets" list: managers can save any
 * combination of address + subtotal + phone as a preset for one-click
 * repeat testing. Presets persist on the store_settings doc.
 */
const TestDeliveryCard = () => {
  const [address, setAddress] = useState("");
  const [subtotal, setSubtotal] = useState(25);
  const [phone, setPhone] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const [presets, setPresets] = useState([]);
  const [presetLabel, setPresetLabel] = useState("");
  const [presetsLoading, setPresetsLoading] = useState(true);
  const [savingPreset, setSavingPreset] = useState(false);

  const loadPresets = async () => {
    setPresetsLoading(true);
    try {
      const res = await axios.get(`${API}/delivery/test-presets`);
      setPresets(res.data || []);
    } catch {
      // silent — feature is optional
    } finally {
      setPresetsLoading(false);
    }
  };
  useEffect(() => { loadPresets(); }, []);

  const applyPreset = (p) => {
    setAddress(p.address || "");
    setSubtotal(p.subtotal ?? 25);
    setPhone(p.customer_phone || "");
    setResult(null);
    toast.success(`Loaded "${p.label}"`);
  };

  const savePreset = async () => {
    if (!presetLabel.trim() || !address.trim()) {
      toast.error("Enter a label and a test address");
      return;
    }
    setSavingPreset(true);
    try {
      await axios.post(`${API}/delivery/test-presets`, {
        label: presetLabel.trim(),
        address: address.trim(),
        subtotal: Number(subtotal) || 25,
        customer_phone: phone.trim(),
      });
      toast.success(`Preset "${presetLabel}" saved`);
      setPresetLabel("");
      loadPresets();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save preset");
    } finally {
      setSavingPreset(false);
    }
  };

  const removePreset = async (id) => {
    if (!window.confirm("Delete this preset?")) return;
    try {
      await axios.delete(`${API}/delivery/test-presets/${id}`);
      loadPresets();
    } catch {
      toast.error("Delete failed");
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/delivery/test-order`, {
        address, subtotal: Number(subtotal),
        customer_phone: phone || null,
        customer_name: "TEST Customer",
      });
      setResult(res.data);
      toast.success("Test order queued");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Send Test Delivery</CardTitle>
        <CardDescription>
          Drops a fake delivery in the queue so you can watch the whole
          flow — a driver picks it up, sends the SMS, and the customer
          tracks it live. Test orders are flagged so they never appear
          on real reports.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Presets */}
        <div className="space-y-2" data-testid="test-delivery-presets">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Saved Presets
            </Label>
            {presetsLoading && <span className="text-xs text-muted-foreground">Loading…</span>}
          </div>
          {presets.length === 0 && !presetsLoading && (
            <div className="text-xs text-muted-foreground">
              No presets yet. Fill in the fields below and tap <b>Save as preset</b> to keep this address on speed dial.
            </div>
          )}
          {presets.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {presets.map((p) => (
                <div
                  key={p.id}
                  className="group inline-flex items-center gap-1 rounded-full border pl-3 pr-1 py-1 text-xs bg-secondary/40 hover:bg-secondary transition-colors"
                  data-testid={`preset-${p.id}`}
                >
                  <button
                    type="button"
                    onClick={() => applyPreset(p)}
                    className="flex items-center gap-1"
                    data-testid={`preset-apply-${p.id}`}
                  >
                    <span className="font-medium">{p.label}</span>
                    <span className="text-muted-foreground truncate max-w-[160px]">
                      — {p.address}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => removePreset(p.id)}
                    className="ml-1 text-muted-foreground hover:text-destructive w-5 h-5 rounded-full inline-flex items-center justify-center"
                    aria-label="Delete preset"
                    data-testid={`preset-delete-${p.id}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div className="space-y-1 md:col-span-2">
            <Label>Test Address</Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)}
              placeholder="123 Main St, City, State ZIP"
              data-testid="test-delivery-address" />
          </div>
          <div className="space-y-1">
            <Label>Subtotal ($)</Label>
            <Input type="number" step="0.01" min="0" value={subtotal}
              onChange={(e) => setSubtotal(e.target.value)}
              data-testid="test-delivery-subtotal" />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label>Your Phone (optional — receives the tracking SMS)</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="+15551234567"
              data-testid="test-delivery-phone" />
          </div>
          <div className="space-y-1">
            <Label>Save as preset (label)</Label>
            <div className="flex gap-1">
              <Input value={presetLabel} onChange={(e) => setPresetLabel(e.target.value)}
                placeholder='"Downtown apt"'
                data-testid="test-preset-label" />
              <Button
                size="sm"
                variant="outline"
                onClick={savePreset}
                disabled={savingPreset || !presetLabel.trim() || !address.trim()}
                data-testid="test-preset-save"
              >
                Save
              </Button>
            </div>
          </div>
        </div>
        <Button onClick={submit} disabled={busy || !address}
          data-testid="test-delivery-send" className="gap-2">
          <Truck size={16} weight="bold" />
          {busy ? "Placing…" : "Send Test Delivery"}
        </Button>
        {result && (
          <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-3 text-sm space-y-1"
               data-testid="test-delivery-result">
            <div><b>Order #{result.order_id.slice(-6)}</b> placed — ${result.order.total.toFixed(2)}</div>
            <div className="text-xs text-muted-foreground">
              Distance {result.order.delivery_distance_mi?.toFixed(1)} mi · fee ${result.order.delivery_fee.toFixed(2)}
              {result.order.delivery_zone_name && ` · zone: ${result.order.delivery_zone_name}`}
            </div>
            <a href={result.track_url} target="_blank" rel="noreferrer"
               className="text-primary text-xs underline">
              Open tracking page →
            </a>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
